import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Wand2, ChevronRight, ChevronLeft, Check, Settings, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import sampleDataService from '../../services/sampleDataService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';

export default function SampleDataGenerator() {
  const { isSuperAdmin } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState({
    scenario: 'general',
    entities: {
      contacts: { enabled: true, count: 50 },
      deals: { enabled: true, count: 30 },
      customers: { enabled: true, count: 40 },
      activities: { enabled: true, count: 100 },
      services: { enabled: true, count: 20 },
      organizations: { enabled: false, count: 10 },
      appointments: { enabled: true, count: 60 },
      invoices: { enabled: true, count: 35 }
    },
    relationshipDensity: 'medium',
    dataQuality: 'high'
  });

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadTemplates();
  }, [isSuperAdmin]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await sampleDataService?.getAllTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEntityToggle = (entityType) => {
    setConfig(prev => ({
      ...prev,
      entities: {
        ...prev?.entities,
        [entityType]: {
          ...prev?.entities?.[entityType],
          enabled: !prev?.entities?.[entityType]?.enabled
        }
      }
    }));
  };

  const handleCountChange = (entityType, count) => {
    setConfig(prev => ({
      ...prev,
      entities: {
        ...prev?.entities,
        [entityType]: {
          ...prev?.entities?.[entityType],
          count: parseInt(count) || 0
        }
      }
    }));
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const enabledEntities = Object.entries(config?.entities)?.filter(([_, cfg]) => cfg?.enabled)?.map(([type, cfg]) => ({ type, count: cfg?.count }));

      for (const entity of enabledEntities) {
        await sampleDataService?.generateSampleData(entity?.type, {
          count: entity?.count,
          scenario: config?.scenario,
          quality: config?.dataQuality
        });
      }

      setCurrentStep(4);
    } catch (error) {
      console.error('Generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only Super Admins can access Sample Data Generator.</p>
        </div>
      </div>
    );
  }

  const scenarios = [
    { value: 'general', label: 'General Business' },
    { value: 'startup', label: 'Startup (Small Scale)' },
    { value: 'smb', label: 'SMB (Medium Scale)' },
    { value: 'enterprise', label: 'Enterprise (Large Scale)' }
  ];

  const relationshipOptions = [
    { value: 'low', label: 'Low (Minimal relationships)' },
    { value: 'medium', label: 'Medium (Balanced)' },
    { value: 'high', label: 'High (Highly connected)' }
  ];

  const qualityOptions = [
    { value: 'basic', label: 'Basic (Simple data)' },
    { value: 'high', label: 'High (Realistic data)' },
    { value: 'premium', label: 'Premium (Complex scenarios)' }
  ];

  const entityIcons = {
    contacts: 'Users',
    deals: 'TrendingUp',
    customers: 'Building2',
    activities: 'Activity',
    services: 'Briefcase',
    organizations: 'Building',
    appointments: 'Calendar',
    invoices: 'FileText'
  };

  return (
    <>
      <Helmet>
        <title>Sample Data Generator - CRM</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Wand2 className="w-8 h-8 text-purple-600" />
            Sample Data Generator
          </h1>
          <p className="text-gray-600 mt-1">Create realistic sample data with customizable parameters</p>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4]?.map((step) => (
              <React.Fragment key={step}>
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {currentStep > step ? <Check className="w-5 h-5" /> : step}
                  </div>
                  <span className={`ml-3 font-medium ${
                    currentStep >= step ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step === 1 && 'Select Scenario'}
                    {step === 2 && 'Configure Entities'}
                    {step === 3 && 'Review & Generate'}
                    {step === 4 && 'Complete'}
                  </span>
                </div>
                {step < 4 && (
                  <ChevronRight className="w-5 h-5 text-gray-400 mx-4" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Step 1: Scenario Selection */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Generation Scenario</h2>
              <p className="text-gray-600 mb-6">Choose a scenario that matches your testing needs</p>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Scenario Type</label>
                  <Select
                    value={config?.scenario}
                    onChange={(e) => setConfig({ ...config, scenario: e?.target?.value })}
                  >
                    {scenarios?.map(s => (
                      <option key={s?.value} value={s?.value}>{s?.label}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Quality</label>
                  <Select
                    value={config?.dataQuality}
                    onChange={(e) => setConfig({ ...config, dataQuality: e?.target?.value })}
                  >
                    {qualityOptions?.map(q => (
                      <option key={q?.value} value={q?.value}>{q?.label}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relationship Density</label>
                  <Select
                    value={config?.relationshipDensity}
                    onChange={(e) => setConfig({ ...config, relationshipDensity: e?.target?.value })}
                  >
                    {relationshipOptions?.map(r => (
                      <option key={r?.value} value={r?.value}>{r?.label}</option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setCurrentStep(2)}>
                  Next: Configure Entities
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Entity Configuration */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Configure Entities</h2>
              <p className="text-gray-600 mb-6">Select entities and specify record counts</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {Object.entries(config?.entities)?.map(([entityType, entityConfig]) => (
                  <div key={entityType} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={entityConfig?.enabled}
                          onChange={() => handleEntityToggle(entityType)}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <div className="flex items-center gap-2">
                          <Icon name={entityIcons?.[entityType]} className="w-5 h-5 text-gray-600" />
                          <span className="font-medium text-gray-900 capitalize">{entityType}</span>
                        </div>
                      </div>
                    </div>
                    {entityConfig?.enabled && (
                      <div className="ml-8">
                        <label className="block text-sm text-gray-600 mb-1">Record Count</label>
                        <Input
                          type="number"
                          value={entityConfig?.count}
                          onChange={(e) => handleCountChange(entityType, e?.target?.value)}
                          min="1"
                          max="1000"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={() => setCurrentStep(3)}>
                  Next: Review
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Generate */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Review Configuration</h2>
              <p className="text-gray-600 mb-6">Verify your settings before generating sample data</p>

              <div className="space-y-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Scenario Settings</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-600">Scenario:</span> <span className="font-medium">{scenarios?.find(s => s?.value === config?.scenario)?.label}</span></p>
                    <p><span className="text-gray-600">Data Quality:</span> <span className="font-medium">{qualityOptions?.find(q => q?.value === config?.dataQuality)?.label}</span></p>
                    <p><span className="text-gray-600">Relationship Density:</span> <span className="font-medium">{relationshipOptions?.find(r => r?.value === config?.relationshipDensity)?.label}</span></p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-2">Selected Entities</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(config?.entities)?.filter(([_, cfg]) => cfg?.enabled)?.map(([type, cfg]) => (
                        <div key={type} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 capitalize">{type}:</span>
                          <span className="font-medium">{cfg?.count} records</span>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Total Records:</strong> {Object.values(config?.entities)?.filter(cfg => cfg?.enabled)?.reduce((sum, cfg) => sum + cfg?.count, 0)} records will be generated
                  </p>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)} disabled={generating}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={handleGenerate} disabled={generating}>
                  {generating ? 'Generating...' : 'Generate Sample Data'}
                  <Wand2 className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {currentStep === 4 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Generation Complete!</h2>
              <p className="text-gray-600 mb-6">Sample data has been successfully generated</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  Generate More
                </Button>
                <Button onClick={() => window.location.href = '/sample-data-management-dashboard'}>
                  View Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
